import React from "react";

import {
  Checkbox,
  DefaultButton,
  Heading,
  IContextualMenuItem,
  IContextualMenuItemProps,
  Stack,
  toKebabCase
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { NzPracticeAccContractDto } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { SpecifiedTreatmentFields } from "@modules/settings/screens/practice/components/practice-acc-contracts/SpecifiedTreatmentFields.tsx";
import { SpecifiedTreatmentFieldsWrapper } from "@modules/settings/screens/practice/components/practice-acc-contracts/SpecifiedTreatmentFieldsWrapper.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { DeleteButton } from "@ui-components/DeleteButton.tsx";
import { useFieldArray } from "@ui-components/form/submission-form/hooks/useFieldArray.ts";
import { TextInputField } from "@ui-components/form/TextInputField.tsx";

import { AccSetupFormValues } from "../health-certificate/AccSetupForm.tsx";
import {
  AccPracticeContractLabels,
  AccPracticeContractType,
  PracticeDetailsFormLabels
} from "../practice-details/PracticeDetailsForm.types.tsx";
import { PracticeAccContractsStyles } from "./PracticeAccContracts.styles.tsx";
import { PracticeAccContractsNoDataTile } from "./PracticeAccContractsNoDataTile.tsx";

export const nameOfAccSetupForm = nameOfFactory<AccSetupFormValues>();

export const PracticeAccContracts: React.FC = () => {
  const {
    core,
    practice: {
      ref: { accPracticeContractTypes: practiceContractTypeRef }
    }
  } = useStores();

  const readOnly = !core.hasPermissions(Permission.OrgUnitSettingWrite);

  const practiceAccContractFields =
    useFieldArray<NzPracticeAccContractDto>("accContracts").fields;

  const stpPracticeAccContractFields =
    useFieldArray<NzPracticeAccContractDto>("stpAccContracts").fields;

  const addItem = (key: string) => {
    const value = {
      accPracticeContractTypeCode: key,
      contractId: "",
      isSubscribed: false
    };

    const isStpPracticeAccFields =
      key === AccPracticeContractType.SpecifiedTreatmentProvider;
    if (isStpPracticeAccFields) {
      stpPracticeAccContractFields.push(value);
    } else {
      if (key === AccPracticeContractType.AlliedHeathServices) {
        practiceAccContractFields.unshift(value);
      } else {
        practiceAccContractFields.push(value);
      }
    }
  };

  const removeItem = (key: string) => {
    const isStpPracticeAccFields =
      key === AccPracticeContractType.SpecifiedTreatmentProvider;

    if (isStpPracticeAccFields) {
      const stpPracticeAccIndex = stpPracticeAccContractFields.value.findIndex(
        i => i.accPracticeContractTypeCode === key
      );
      stpPracticeAccContractFields.remove(stpPracticeAccIndex);
    } else {
      const practiceAccIndex = practiceAccContractFields.value.findIndex(
        i => i.accPracticeContractTypeCode === key
      );
      practiceAccContractFields.remove(practiceAccIndex);
    }
  };

  const getAddMoreItems = (): IContextualMenuItem[] =>
    practiceContractTypeRef.keyTextValues.map(({ key, text }) => {
      const isStpPracticeAccFields =
        key === AccPracticeContractType.SpecifiedTreatmentProvider;
      return {
        canCheck: true,
        checked: isStpPracticeAccFields
          ? stpPracticeAccContractFields.value.some(
              i => i.accPracticeContractTypeCode === key
            )
          : practiceAccContractFields.value.some(
              i => i.accPracticeContractTypeCode === key
            ),
        key,
        text,
        onClick: (_, option: IContextualMenuItem) => {
          option.checked = !option.checked;
          if (option.checked) {
            addItem(option.key);
          } else {
            removeItem(option.key);
          }
        }
      };
    });

  return (
    <Stack styles={PracticeAccContractsStyles.root}>
      <Stack
        horizontal
        horizontalAlign="space-between"
        styles={PracticeAccContractsStyles.heading}
      >
        <Heading variant="section-heading-light">
          {PracticeDetailsFormLabels.accContracts}
        </Heading>
        {(practiceAccContractFields.value.length > 0 ||
          stpPracticeAccContractFields.value.length > 0) && (
          <DefaultButton
            disabled={readOnly}
            iconProps={{ iconName: PracticeDetailsFormLabels.add }}
            text={PracticeDetailsFormLabels.addMore}
            menuProps={{
              styles: PracticeAccContractsStyles.buttonMenuProps,
              shouldFocusOnMount: false,
              items: getAddMoreItems(),
              contextualMenuItemAs: (props: IContextualMenuItemProps) => (
                <Checkbox
                  automationAttribute={
                    props.item.text
                      ? toKebabCase(props.item.text)
                      : "no-text-acc-contractor-checkbox"
                  }
                  label={props.item.text}
                  defaultChecked={props.item.checked}
                />
              )
            }}
          />
        )}
      </Stack>

      <Stack tokens={{ childrenGap: 10 }}>
        {practiceAccContractFields.map((name, index) => {
          const labelKey =
            practiceAccContractFields.value[index].accPracticeContractTypeCode;

          return (
            <Stack
              key={name}
              horizontal
              tokens={{ childrenGap: 10 }}
              styles={PracticeAccContractsStyles.contractTypeItem}
            >
              <TextInputField
                readOnly={readOnly}
                label={AccPracticeContractLabels[labelKey]}
                name={`${nameOfAccSetupForm(
                  "accContracts"
                )}[${index}].contractId`}
              />
              <DeleteButton
                styles={PracticeAccContractsStyles.deleteButton}
                onClick={() => practiceAccContractFields.remove(index)}
                disabled={readOnly}
              />
            </Stack>
          );
        })}

        {stpPracticeAccContractFields.value?.length > 0 && (
          <SpecifiedTreatmentFieldsWrapper
            addItem={stpPracticeAccContractFields.push}
          >
            {businessRoleCodes =>
              stpPracticeAccContractFields.map((name, index) => (
                <SpecifiedTreatmentFields
                  key={name}
                  handleKeyRemove={() => {
                    stpPracticeAccContractFields.remove(index);
                  }}
                  businessRoleCodes={businessRoleCodes}
                  name={name}
                />
              ))
            }
          </SpecifiedTreatmentFieldsWrapper>
        )}
      </Stack>
      <PracticeAccContractsNoDataTile
        onTileItemClicked={addItem}
        show={
          !practiceAccContractFields.value.length &&
          !stpPracticeAccContractFields.value.length
        }
      />
    </Stack>
  );
};
